import { Graphics } from "pixi.js";

interface FeatheredEllipseOptions {
  x?: number;
  y?: number;
  radiusX: number;
  radiusY: number;
  color: string;
  alpha: number;
  feather?: number;
  layers?: number;
}

/**
 * Builds a soft-edged ellipse from concentric WebGL geometry. The outermost
 * band is almost transparent, while the combined center reaches the requested
 * opacity. This is cheaper and more predictable than allocating a blur filter
 * texture for every movable prop.
 */
export function createFeatheredEllipse({ x = 0, y = 0, radiusX, radiusY, color, alpha, feather = 0.48, layers = 12 }: FeatheredEllipseOptions): Graphics {
  const graphic = new Graphics();
  const layerCount = Math.max(4, Math.round(layers));
  const clampedAlpha = Math.max(0, Math.min(0.98, alpha));
  const clampedFeather = Math.max(0.15, Math.min(0.82, feather));
  const totalWeight = layerCount * (layerCount + 1) / 2;
  const opticalDensity = -Math.log(1 - clampedAlpha);

  for (let index = 0; index < layerCount; index += 1) {
    const progress = layerCount === 1 ? 0 : index / (layerCount - 1);
    const scale = 1 - clampedFeather * progress;
    const layerAlpha = 1 - Math.exp(-opticalDensity * (index + 1) / totalWeight);
    graphic.ellipse(x, y, radiusX * scale, radiusY * scale).fill({ color, alpha: layerAlpha });
  }

  return graphic;
}
